// Service health card — shows one service's status, response time, probes

import { clsx } from 'clsx'
import { CheckCircle2, ChevronDown, ChevronUp, XCircle } from 'lucide-react'
import { useState } from 'react'
import { StatusBadge } from '@/components/ui/status-badge'
import type { ServiceStatus } from '@/lib/types-status'

interface ServiceCardProps {
  service: ServiceStatus
}

function formatResponseTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(2)}s`
}

function statusDotColor(status: string): string {
  if (status === 'operational') return 'bg-emerald-500'
  if (status === 'degraded') return 'bg-yellow-500'
  if (status === 'major_outage') return 'bg-red-500'
  return 'bg-gray-400'
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  return `${Math.floor(seconds / 3600)}h ago`
}

export function ServiceCard({ service }: ServiceCardProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 shadow-sm transition-shadow hover:shadow-md">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between p-5"
      >
        <div className="flex items-center gap-3">
          <div className={clsx('h-3 w-3 rounded-full', statusDotColor(service.status))} />
          <div className="text-left">
            <h3 className="text-sm font-semibold text-text-primary">{service.displayName}</h3>
            <p className="mt-0.5 text-xs text-text-tertiary">
              {formatResponseTime(service.responseTimeMs)} &middot; checked{' '}
              {timeAgo(service.lastCheckedAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={service.status} />
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-text-tertiary" />
          ) : (
            <ChevronDown className="h-4 w-4 text-text-tertiary" />
          )}
        </div>
      </button>

      {expanded && service.probes.length > 0 && (
        <div className="border-t border-surface-3 px-5 py-3 space-y-2">
          {service.probes.map((probe) => (
            <div key={probe.name} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                {probe.passed ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-red-500" />
                )}
                <span className="text-text-primary">{probe.name}</span>
              </div>
              <div className="flex items-center gap-3">
                {probe.message && <span className="text-text-tertiary">{probe.message}</span>}
                <span className="font-medium text-text-secondary">
                  {formatResponseTime(probe.latencyMs)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
